import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "../App";
import {
  X, Gavel, DollarSign, CheckCircle, XCircle, Award,
  AlertCircle, Clock, Send, Loader2, HelpCircle, Activity, ArrowUp, Megaphone,
  Bell, ShoppingCart, Undo2, Menu
} from "lucide-react";
import NotificationsPopup from "./NotificationsPopup";
import CartPopup from "./CartPopup";

const ACTIVITY_ICONS = {
  bid: Gavel, outbid: Gavel, new_bid: Gavel,
  offer: DollarSign, counter_offer: DollarSign,
  offer_accepted: CheckCircle, offer_rejected: XCircle,
  auction_won: Award, auction_lost: AlertCircle, auction_expired: Clock,
  intent_accepted: CheckCircle, intent_rejected: XCircle,
  purchase_confirmed: CheckCircle, purchase_paid: CheckCircle, purchase_requested: Activity,
  promo: Megaphone,
};

// Alertas que requieren una decisión del usuario: se resaltan con un botón de acción rápida
// IMPORTANTE: si se agrega un nuevo notif_type que requiera acción en server.py, añadirlo también aquí
const ACTION_LABELS = {
  outbid: "Pujar de nuevo",
  offer: "Responder oferta",
  counter_offer: "Responder oferta",
  purchase_intent: "Responder solicitud",
};

const getGuestSessionId = () => {
  let id = localStorage.getItem("support_chat_guest_id");
  if (!id) {
    id = `guest_${Math.random().toString(36).slice(2)}${Date.now()}`;
    localStorage.setItem("support_chat_guest_id", id);
  }
  return id;
};

// Pequeño botón "volver al menú" - resaltado en naranja (color de acento de la marca) y más
// chico que el resto del pop-up, pegado a la esquina inferior derecha (cerca del botón
// principal) para que sea fácil de asociar y encontrar al querer retroceder
const BackToMenuButton = ({ onClick, testId }) => (
  <button
    onClick={onClick}
    className="absolute -bottom-3 -right-3 w-8 h-8 rounded-full bg-[#F97316] hover:bg-[#EA580C] text-white shadow-lg ring-2 ring-white flex items-center justify-center z-10"
    data-testid={testId}
    aria-label="Volver al menú"
  >
    <Undo2 size={14} />
  </button>
);

export const FloatingSupportWidget = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [dialOpen, setDialOpen] = useState(false);
  const [activityOpen, setActivityOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [notifPopupOpen, setNotifPopupOpen] = useState(false);
  const [cartPopupOpen, setCartPopupOpen] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [activity, setActivity] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [purchaseActivity, setPurchaseActivity] = useState(0);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  const sessionId = user ? user.user_id : getGuestSessionId();
  const popupOpen = activityOpen || helpOpen || notifPopupOpen || cartPopupOpen;
  const anyOpen = popupOpen || dialOpen;

  const refreshCounts = () => {
    if (!user || !token) return;
    axios.get(`${API}/notifications/unread-count`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => setUnreadCount(res.data.count))
      .catch(() => {});
    if (!user.is_admin) {
      axios.get(`${API}/my/activity-count`, { headers: { Authorization: `Bearer ${token}` } })
        .then((res) => setPurchaseActivity(res.data.count))
        .catch(() => {});
    }
  };

  useEffect(() => {
    refreshCounts();
  }, [user, token]);

  useEffect(() => {
    const onScroll = () => setShowScrollTop(window.scrollY > 400);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (!activityOpen || !user || !token) return;
    axios.get(`${API}/notifications`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => setActivity(res.data))
      .catch(() => {});
  }, [activityOpen]);

  useEffect(() => {
    if (!helpOpen) return;
    axios.get(`${API}/support-chat/history`, { params: { session_id: sessionId } })
      .then((res) => setMessages(res.data.messages || []))
      .catch(() => {});
  }, [helpOpen]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages]);

  // One tap on the main button always closes everything that's open (dial, panels or popups)
  const handleMainClick = () => {
    if (anyOpen) {
      setActivityOpen(false);
      setHelpOpen(false);
      setNotifPopupOpen(false);
      setCartPopupOpen(false);
      setDialOpen(false);
      return;
    }
    setDialOpen(true);
  };

  const openActivity = () => { setActivityOpen(true); setDialOpen(false); };
  const openHelp = () => { setHelpOpen(true); setDialOpen(false); };
  const openNotifications = () => { setNotifPopupOpen(true); setDialOpen(false); };
  const openCart = () => { setCartPopupOpen(true); setDialOpen(false); };

  // "Volver al menú": cierra lo que esté abierto y vuelve a mostrar el desplegable
  const backToMenu = (closer) => () => { closer(false); setDialOpen(true); };

  const handleActivityClick = (n) => {
    setActivityOpen(false);
    if (n.action_url) navigate(n.action_url);
  };

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setInput("");
    setSending(true);
    setMessages((prev) => [...prev, { role: "user", content: text }, { role: "assistant", content: "" }]);

    try {
      const response = await axios.post(`${API}/support-chat/message`, { session_id: sessionId, message: text });
      setMessages((prev) => {
        const copy = [...prev];
        copy[copy.length - 1] = { role: "assistant", content: response.data.reply };
        return copy;
      });
    } catch (error) {
      setMessages((prev) => {
        const copy = [...prev];
        copy[copy.length - 1] = { role: "assistant", content: "Disculpa, hubo un problema de conexión. Intenta de nuevo." };
        return copy;
      });
    } finally {
      setSending(false);
    }
  };

  const showDial = dialOpen && !popupOpen;

  return (
    <div className="floating-widget-root">
      {/* Standalone scroll-to-top button - independent of the main widget, fades in/out smoothly.
          Sits above the standalone cart button (same column). */}
      <button
        onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
        className={`fixed bottom-36 right-20 sm:bottom-24 sm:right-24 w-11 h-11 rounded-full bg-[#0F172A]/85 hover:bg-[#0F172A] backdrop-blur-sm shadow-xl flex items-center justify-center text-white transition-all duration-300 ease-out ${
          showScrollTop ? "opacity-100 scale-100 pointer-events-auto" : "opacity-0 scale-75 pointer-events-none"
        }`}
        style={{ zIndex: 290 }}
        data-testid="fab-scroll-top"
        aria-label="Subir arriba"
      >
        <ArrowUp size={18} />
      </button>

      {/* Standalone cart button - keeps its own position (to the left of the main button,
          not stacked inside the vertical dial column), but appears/disappears together
          with the dial: visible only while the menu is open */}
      {user && !user.is_admin && (
        <button
          onClick={openCart}
          className={`fixed bottom-20 right-20 sm:bottom-6 sm:right-24 w-11 h-11 rounded-full bg-[#0F172A]/85 hover:bg-[#0F172A] backdrop-blur-sm shadow-xl flex items-center justify-center text-white transition-all duration-300 ease-out ${
            showDial ? "opacity-100 scale-100 pointer-events-auto" : "opacity-0 scale-75 pointer-events-none"
          }`}
          style={{ zIndex: 290 }}
          data-testid="fab-cart"
          aria-label="Carrito"
        >
          <ShoppingCart size={18} />
          {purchaseActivity > 0 && (
            <span className="absolute -top-1 -right-1 bg-[#EF4444] text-white text-[9px] font-bold rounded-full min-w-[15px] h-[15px] flex items-center justify-center px-0.5">
              {purchaseActivity > 9 ? "9+" : purchaseActivity}
            </span>
          )}
        </button>
      )}

      {/* Dial: tapping the main menu button reveals Notificaciones / Actividades / Ayuda,
          all fading in together (no stagger) for a smoother, less "flickery" feel */}
      <div
        className={`fixed bottom-36 right-4 sm:bottom-24 sm:right-6 z-[200] flex flex-col items-end gap-3 transition-all duration-300 ease-out ${
          showDial ? "opacity-100 translate-y-0 pointer-events-auto" : "opacity-0 translate-y-3 pointer-events-none"
        }`}
        data-testid="fab-dial"
      >
        {user && (
          <button
            onClick={openNotifications}
            className="relative w-12 h-12 rounded-full bg-[#0F172A] text-white shadow-xl flex items-center justify-center"
            data-testid="fab-notifications"
            aria-label="Notificaciones"
          >
            <Bell size={18} />
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 bg-[#F97316] text-white text-[9px] font-bold rounded-full min-w-[15px] h-[15px] flex items-center justify-center px-0.5">
                {unreadCount > 9 ? "9+" : unreadCount}
              </span>
            )}
          </button>
        )}
        <button
          onClick={openActivity}
          className="w-12 h-12 rounded-full bg-[#0F172A] text-white shadow-xl flex items-center justify-center"
          data-testid="fab-activity"
          aria-label="Actividad"
        >
          <Activity size={18} />
        </button>
        <button
          onClick={openHelp}
          className="w-12 h-12 rounded-full bg-[#25D366] hover:bg-[#1DA851] text-white shadow-xl flex items-center justify-center"
          data-testid="fab-support"
          aria-label="Ayuda"
        >
          <HelpCircle size={18} />
        </button>
      </div>

      <button
        onClick={handleMainClick}
        className="fixed bottom-20 right-4 sm:bottom-6 sm:right-6 w-11 h-11 rounded-full bg-[#0F172A]/85 hover:bg-[#0F172A] backdrop-blur-sm shadow-xl flex items-center justify-center text-white transition-all pointer-events-auto"
        style={{ zIndex: 300 }}
        data-testid="floating-support-button"
        aria-label="Menú flotante"
      >
        {anyOpen ? <X size={18} /> : <Menu size={18} />}
        {!anyOpen && user && (unreadCount + purchaseActivity) > 0 && (
          <span className="absolute -top-1 -right-1 bg-[#F97316] text-white text-[9px] font-bold rounded-full min-w-[15px] h-[15px] flex items-center justify-center px-0.5" data-testid="floating-support-badge">
            {(unreadCount + purchaseActivity) > 9 ? "9+" : unreadCount + purchaseActivity}
          </span>
        )}
      </button>

      {activityOpen && (
        <div
          className="fixed bottom-36 right-4 sm:bottom-24 sm:right-6 z-[200] w-[90vw] max-w-[340px] h-[70vh] max-h-[480px]"
          data-testid="floating-activity-panel"
        >
          <BackToMenuButton onClick={backToMenu(setActivityOpen)} testId="activity-back-to-menu" />
          <div className="w-full h-full bg-white/95 backdrop-blur-md rounded-2xl shadow-2xl border border-[#E2E8F0] flex flex-col overflow-hidden">
            <div className="flex items-center gap-1.5 py-3 px-4 border-b border-[#E2E8F0] bg-white/80 text-sm font-semibold text-[#0F172A]">
              <Activity size={16} />
              Actividad
            </div>
            <div className="flex-1 overflow-y-auto p-3 space-y-2" data-testid="floating-activity-list">
              {!user ? (
                <p className="text-sm text-[#94A3B8] text-center py-10">Inicia sesión para ver tu actividad en productos.</p>
              ) : activity.length === 0 ? (
                <p className="text-sm text-[#94A3B8] text-center py-10">Aún no tienes actividad. Participa en una subasta u oferta para verla aquí.</p>
              ) : (
                activity.map((n) => {
                  const Icon = ACTIVITY_ICONS[n.type] || Activity;
                  const actionLabel = n.action_url ? ACTION_LABELS[n.type] : null;
                  return (
                    <button
                      key={n.notification_id}
                      onClick={() => handleActivityClick(n)}
                      className={`w-full text-left flex gap-2.5 p-2.5 rounded-xl hover:bg-[#F8FAFC] transition-colors ${!n.read ? "bg-[#F1F5F9]" : ""}`}
                      data-testid={`floating-activity-item-${n.notification_id}`}
                    >
                      <div className="w-8 h-8 rounded-full bg-[#0F172A]/5 flex items-center justify-center flex-shrink-0">
                        <Icon size={15} className="text-[#0F172A]" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-[#334155] leading-snug">{n.message}</p>
                        {actionLabel && (
                          <span
                            className="inline-block mt-1.5 text-[10px] font-bold text-white bg-[#F97316] px-2.5 py-1 rounded-full"
                            data-testid={`floating-activity-action-${n.notification_id}`}
                          >
                            {actionLabel} →
                          </span>
                        )}
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}

      {helpOpen && (
        <div
          className="fixed bottom-36 right-4 sm:bottom-24 sm:right-6 z-[200] w-[90vw] max-w-[340px] h-[70vh] max-h-[480px]"
          data-testid="floating-help-panel"
        >
          <BackToMenuButton onClick={backToMenu(setHelpOpen)} testId="help-back-to-menu" />
          <div className="w-full h-full bg-white/95 backdrop-blur-md rounded-2xl shadow-2xl border border-[#E2E8F0] flex flex-col overflow-hidden">
            <div className="flex items-center gap-1.5 py-3 px-4 border-b border-[#E2E8F0] text-sm font-semibold text-[#075E54]">
              <HelpCircle size={16} />
              Ayuda
            </div>
            <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 space-y-2.5 bg-[#ECFDF3]" data-testid="floating-chat-messages">
              {messages.length === 0 && (
                <p className="text-xs text-[#4B5563] text-center py-6">
                  ¡Hola! Pregúntame sobre subastas, ofertas, pago contraentrega o cashback con Yappy.
                </p>
              )}
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    className={`max-w-[85%] px-3 py-2 rounded-2xl text-xs leading-relaxed shadow-sm ${
                      m.role === "user" ? "bg-[#DCF8C6] text-[#111B21]" : "bg-white text-[#111B21] border border-[#E5E7EB]"
                    }`}
                    data-testid={`floating-chat-bubble-${m.role}-${i}`}
                  >
                    {m.content || <Loader2 size={12} className="animate-spin" />}
                  </div>
                </div>
              ))}
            </div>
            <div className="flex items-center gap-2 p-2.5 border-t border-[#E2E8F0] bg-white">
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && sendMessage()}
                placeholder="Escribe tu pregunta..."
                className="flex-1 text-xs px-3 py-2 rounded-full border border-[#E2E8F0] outline-none focus:border-[#25D366]"
                data-testid="floating-chat-input"
              />
              <button
                onClick={sendMessage}
                disabled={sending || !input.trim()}
                className="w-9 h-9 rounded-full bg-[#25D366] hover:bg-[#1DA851] text-white flex items-center justify-center disabled:opacity-40 flex-shrink-0"
                data-testid="floating-chat-send"
              >
                <Send size={14} />
              </button>
            </div>
          </div>
        </div>
      )}

      <NotificationsPopup
        open={notifPopupOpen}
        onClose={() => { setNotifPopupOpen(false); refreshCounts(); }}
        onBack={backToMenu(setNotifPopupOpen)}
      />
      <CartPopup
        open={cartPopupOpen}
        onClose={() => { setCartPopupOpen(false); refreshCounts(); }}
        onBack={backToMenu(setCartPopupOpen)}
      />
    </div>
  );
};

export default FloatingSupportWidget;
